import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { AdminCrudPage, AdminCrudPageConfig } from '@/components/admin';
import api, { getErrorMessage } from '@/services/api';

/**
 * IRDAI's own lines of business, verbatim from the Form S returns (mirrors
 * `InsuranceType.IRDAI_LINE_CHOICES`). These are the REGULATOR's lines, not
 * this platform's taxonomy: we sell SME, Professional and Cyber; the regulator
 * counts Fire, Liability and Marine. Blank means "not yet classified", and a
 * type left blank is reported as unclassified on the returns rather than being
 * folded into Miscellaneous.
 */
const IRDAI_LINE_OPTIONS = [
  { value: '', label: 'Not classified' },
  { value: 'engineering', label: 'Engineering' },
  { value: 'fire', label: 'Fire' },
  { value: 'health', label: 'Health' },
  { value: 'liability', label: 'Liability' },
  { value: 'marine_cargo', label: 'Marine Cargo' },
  { value: 'marine_hull', label: 'Marine Hull' },
  { value: 'motor', label: 'Motor' },
  { value: 'misc', label: 'Others / Miscellaneous' },
  { value: 'life', label: 'Life (life business)' },
  { value: 'life_health', label: 'Health (life business)' },
  { value: 'pension', label: 'Pension' },
  { value: 'annuity', label: 'Annuity' },
];

/**
 * The FILTER's version of the same list, with one difference: "Not classified"
 * carries the sentinel `unclassified` instead of `''`.
 *
 * `AdminCrudPage`'s filter bar treats an empty string as "no filter selected"
 * (see `buildListUrl`, which drops falsy filter values so the query string
 * never grows a stray `?irdai_line=`), so a literal `''` here would be
 * indistinguishable from clearing the filter and could never actually narrow
 * the list to the unclassified rows -- exactly the question this page's own
 * docstring below says an admin needs answered before filing a return.
 * `InsuranceTypeViewSet.get_queryset` resolves the sentinel back to the real
 * `irdai_line=""` filter server-side.
 */
const IRDAI_LINE_FILTER_OPTIONS = IRDAI_LINE_OPTIONS.map((opt) =>
  opt.value === '' ? { value: 'unclassified', label: 'Not classified' } : opt
);

const STATUS_FILTER_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// Only meaningful for the four life lines, and REQUIRED for them: Form S (Life)
// has a Linked half and a Non-Linked half, so a life line without this cannot
// be placed on any row. The backend rejects the invalid combinations either way
// (`InsuranceTypeSerializer.validate`), so this is a three-state choice rather
// than a checkbox -- a checkbox cannot express "not stated".
const IRDAI_LINKED_OPTIONS = [
  { value: '', label: 'Not stated (general insurance)' },
  { value: 'true', label: 'Linked' },
  { value: 'false', label: 'Non-Linked' },
];

type InsuranceTypeRow = {
  id: string;
  name: string;
  code: string;
  description: string;
  irdai_line: string;
  irdai_line_display: string;
  irdai_is_linked: boolean | null;
  is_active: boolean;
  groups_count: number;
  [key: string]: unknown;
};

/**
 * Inline Active/Inactive switch for a single row (item 6).
 *
 * `hasActivate` already gives a BULK activate/deactivate once rows are ticked,
 * but flipping just one type used to mean opening the edit form for a single
 * checkbox. This calls the same `PATCH is_active` the edit form would, from
 * the list itself, and invalidates the same query key so the row (and the
 * groups/lookup caches downstream) reflect it immediately.
 */
function StatusToggle({ row }: { row: InsuranceTypeRow }) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (nextActive: boolean) => {
      await api.patch(`/products/types/${row.id}/`, { is_active: nextActive });
    },
    onSuccess: (_data, nextActive) => {
      queryClient.invalidateQueries({ queryKey: ['insuranceTypes'] });
      toast.success(`${row.name} is now ${nextActive ? 'active' : 'inactive'}`);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  return (
    <button
      type="button"
      role="switch"
      aria-checked={row.is_active}
      aria-label={`${row.is_active ? 'Deactivate' : 'Activate'} ${row.name}`}
      disabled={mutation.isPending}
      onClick={() => mutation.mutate(!row.is_active)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        row.is_active ? 'bg-primary-600' : 'bg-gray-300'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          row.is_active ? 'translate-x-5' : 'translate-x-1'
        }`}
      />
    </button>
  );
}

function InsuranceTypeDetail({ item }: { item: InsuranceTypeRow }) {
  return (
    <dl className="space-y-3 text-sm">
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Name</dt>
        <dd className="text-gray-900 dark:text-white">{item.name}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Code</dt>
        <dd className="text-gray-900 dark:text-white">{item.code}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Description</dt>
        <dd className="whitespace-pre-wrap text-gray-900 dark:text-white">
          {item.description || 'No description.'}
        </dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">IRDAI Line</dt>
        <dd className="text-gray-900 dark:text-white">{item.irdai_line_display || 'Not classified'}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Status</dt>
        <dd className="text-gray-900 dark:text-white">{item.is_active ? 'Active' : 'Inactive'}</dd>
      </div>
      <div>
        <dt className="font-medium text-gray-500 dark:text-gray-400">Groups</dt>
        <dd className="text-gray-900 dark:text-white">{item.groups_count}</dd>
      </div>
    </dl>
  );
}

const config: AdminCrudPageConfig<InsuranceTypeRow> = {
  title: 'Insurance Types',
  apiEndpoint: '/products/types/',
  // Backed by a viewset that mixes in `core.import_export.ImportExportMixin`,
  // so the export/import-template/import routes this turns on really exist.
  hasImportExport: true,
  queryKey: 'insuranceTypes',
  // Item 6 also asks for fixed column headers -- the shared `Table.tsx`
  // primitive grew a `layout="fixed"` option (another lane, G2) and
  // `AdminCrudPage` now has its own copy of the same mechanism, so widths
  // here are what actually turns it on for this screen (`tableLayout: 'fixed'`
  // below). Percentages leave room for the unweighted Actions column.
  columns: [
    { key: 'name', label: 'Name', width: '18%', sortable: true },
    { key: 'code', label: 'Code', width: '12%' },
    { key: 'description', label: 'Description', width: '35%' },
    // Surfaced on the LIST, not just the edit form: the question to answer
    // before filing a return is "what is still unclassified", and only the
    // list can answer it.
    {
      key: 'irdai_line_display',
      label: 'IRDAI Line',
      width: '20%',
      render: (v: unknown) => (v ? String(v) : 'Not classified'),
    },
    {
      key: 'is_active',
      label: 'Status',
      sortable: false,
      width: '10%',
      render: (_v: unknown, row: InsuranceTypeRow) => <StatusToggle row={row} />,
    },
  ],
  tableLayout: 'fixed',
  formFields: [
    { name: 'name', label: 'Name', type: 'text', required: true },
    { name: 'code', label: 'Code', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'text' },
    {
      name: 'irdai_line',
      label: 'IRDAI Line of Business (Form S)',
      type: 'select',
      options: IRDAI_LINE_OPTIONS,
    },
    {
      name: 'irdai_is_linked',
      label: 'Linked / Non-Linked (life business only)',
      type: 'select',
      options: IRDAI_LINKED_OPTIONS,
    },
    { name: 'is_active', label: 'Active', type: 'boolean' },
  ],
  hasActivate: true,
  // Item 6: IRDAI Line and Status, inline with the search bar on one row.
  // `InsuranceTypeViewSet.get_queryset` honours both (`is_active` is a normal
  // filterset field; `irdai_line` is resolved manually, including the
  // `unclassified` sentinel above).
  filters: [
    { key: 'irdai_line', label: 'IRDAI Line', options: IRDAI_LINE_FILTER_OPTIONS },
    { key: 'is_active', label: 'Status', options: STATUS_FILTER_OPTIONS },
  ],
  renderView: (item) => <InsuranceTypeDetail item={item} />,
  viewTitle: (item) => item.name,
};

export function InsuranceTypesPage() {
  return <AdminCrudPage config={config} />;
}
